import React from "react";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import {
  clampManualFocusDistance,
  parseStoredFocusDistance,
} from "../../helpers/focusUtils";
import FocusDistanceSlider from "./FocusDistanceSlider";

interface FocusRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface FocusControlsProps {
  focusMode: string;
  focusDistance: string;
  focusRect: FocusRect | null;
  onFocusModeChange: (mode: string) => void;
  onFocusDistanceChange: (val: string) => void;
  onFocusRectChange: (rect: FocusRect) => void;
  disabled?: boolean;
}

const FocusControls: React.FC<FocusControlsProps> = ({
  focusMode,
  focusDistance,
  onFocusModeChange,
  onFocusDistanceChange,
  disabled = false,
}) => {
  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFocusModeChange(event.target.value);
  };

  const handleDistanceChange = (value: number) => {
    onFocusDistanceChange(String(clampManualFocusDistance(value)));
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Focus Control
      </Typography>
      <RadioGroup value={focusMode} onChange={handleModeChange} row>
        <FormControlLabel
          value="auto"
          control={<Radio size="small" />}
          label="Auto Focus"
          disabled={disabled}
        />
        <FormControlLabel
          value="manual"
          control={<Radio size="small" />}
          label="Manual Focus"
          disabled={disabled}
        />
      </RadioGroup>

      {focusMode === "auto" && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Draw a rectangle on the video to set the auto-focus region.
        </Typography>
      )}

      {focusMode === "manual" && (
        <Box sx={{ mt: 1 }}>
          <FocusDistanceSlider
            value={clampManualFocusDistance(
              parseStoredFocusDistance(focusDistance)
            )}
            onChange={handleDistanceChange}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  );
};

export default FocusControls;
